import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, ReplaySubject, share, Subscription, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AppServiceHelperService } from '../../../services/app-service-helper.service';
import { EventBusService } from '../../../services/event-bus.service';
import { LocalizationService } from '../../app-settings/services/localization-service';
import { ReportDataPreloaderService } from '../services/print/report-data-preloader-service';
import { ReportDesignerStorageService } from '../services/report-designer-storage.service';
import { WidgetInfo } from './widgetInfo';

/**
 * Widget service: Wraps common widget functionality
 */
@Injectable({
  providedIn: 'root'
})
export class WidgetService {

  /**
   * Widget API route
   */
  private _widgetApi = 'reportwidgets';

  /**
   * Widget API Resource
   */
  private _widgetResource: string;

  /**
   * RefList API route
   */
  private _reflistApi = 'reflist/pollux';

  /**
   * Reflist API route to get multiple reflists in one call
   */
  private _reflistsApi = 'reflist/pollux/many';

  /**
   * RefList API Resource
   */
  private _reflistResource: string;

  /**
   * RefList API Resource to get multiple reflists in one call
   */
  private _reflistsResource: string;

  /**
   * Keeps id of the focus el. Needed for hashtag logic
   */
  private focusedElementId: string;

  /**
 * Map of mockup observables for mockup data
 * Used to load mockup data only ones per widget type (f.e. all text widgets get same data using one API call) 
 */
  private mockupObservablesMap = new Map<string, Observable<unknown>>();

  /**
   * Constructor
   * @param httpClient
   * @param localizationService
   * @param appServiceHelper
   * @param eventBus
   * @param reportDataPreloaderService
   * @param rdStorage
   */
  constructor(private httpClient: HttpClient,
    public localizationService: LocalizationService,
    private appServiceHelper: AppServiceHelperService,
    private eventBus: EventBusService,
    private reportDataPreloaderService: ReportDataPreloaderService,
    private rdStorage: ReportDesignerStorageService) {
    this._widgetResource = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', this._widgetApi);
    this._reflistResource = this.appServiceHelper.getServiceEndpoint('pollux.api', this._reflistApi);
    this._reflistsResource = this.appServiceHelper.getServiceEndpoint('pollux.api', this._reflistsApi);
  }

  /**
   * Sets value for the focus el
   */
  setFocusedElementId(id: string) {
    this.focusedElementId = id;
  }

  /**
   * Gets value for the focus el
   */
  getFocusedElementId() {
    return this.focusedElementId;
  }

  /**
   * Loads an empty object. Can be used if widnets don't load data from APIs
   */
  loadEmpty() {
    return of({})
  }

  /**
   * Loads data from the API
   * @param action Action (controller) name
   * @param reportid The report OID
   */
  load(action: string, reportid: string, additionalQueryParams: string = null) {
    let url = `${this._widgetResource}/${action}?id=${reportid}`;
    if (additionalQueryParams) {
      url = `${url}&${additionalQueryParams}`;
    }

    const preloadedData = this.reportDataPreloaderService.getDataForUrl(url);
    return preloadedData ? of(preloadedData) : this.httpClient.get(url);
  }

  /**
   * Loads the (dataverse) mockup data from the API
   * @param action Action (controller) name
   */
  loadMockup(action: string): Observable<unknown> {
    if (!this.rdStorage.getReport()) {
      //in template mode mockup are sometimes used as data (e.g. in text-widget), so always load
      return this.httpClient.get(`${this._widgetResource}/${action}/mockup?id=0`);
    } else {
      //in report mode (and printing) => cache mockups
      let observable$;
      if (this.mockupObservablesMap.has(action)) {
        observable$ = this.mockupObservablesMap.get(action);
      } else {
        //https://indepth.dev/posts/1248/fastest-way-to-cache-for-lazy-developers-angular-with-rxjs
        //https://stackoverflow.com/questions/71392565/what-is-the-replacement-for-publishreplay1-refcount-in-rxjs-version-7-4-0
        observable$ = this.httpClient.get(`${this._widgetResource}/${action}/mockup?id=0`).pipe(
          (source$) => {
            const subject = new ReplaySubject(1);
            return source$.pipe(
              share({
                connector: () => subject,
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: true,
              }),
            );
          }
        );

        this.mockupObservablesMap.set(action, observable$);
      }

      return observable$;
    }
  }

  /**
   * Loads the dataverse documentation from the API
   * @param action Action (controller) name
   */
  loadMapping(action: string) {
    return this.httpClient.get(`${this._widgetResource}/${action}/mapping?docuGuid=${environment.DASHBOARD_WIDGET_DOCUMENTATION_GUID}`);
  }

  /**
   * Loads data from a defined url
   * @param urlPart URL relative to the widget's API
   */
  loadFromWidgetResource(urlPart: string) {
    return this.httpClient.get(`${this._widgetResource}/${urlPart}`);
  }

  /**
   * Loads mockup data from a defined url
   * @param url URL
   */
  loadMockupData(url: string) {
    return this.httpClient.get(url);
  }

  /**
   * Gets the config for a standard A4 V-Page (portrait)
   * @returns
   */
  public getVPageConfig() {
    return {
      "options": {
        "name": "size-a4-z150",
        "isPortrait": true,
        "margins": [0, 5],
        "columns": 6,
        "minRows": 96,
        "maxRows": 96,
        "colWidth": 187.5,
        "rowHeight": 15,
        "floating": false,
        "swapping": false,
        "pushing": false,
        margin: 0,
        outerMarginTop: 0,
        outerMarginRight: 0,
        outerMarginBottom: 0,
        outerMarginLeft: 5,
        outerMargin: true,
        "draggable": {
          "enabled": false
        },
        "resizable": {
          "enabled": false
        }
      },
      "widgets": []
    };
  }

  /**
   * Gets the config for a standard A4 H-Page (landscape)
   * @returns
   */
  public getHPageConfig() {
    return {
      "options": {
        "name": "size-a4-z150-H",
        "isPortrait": false,
        "margins": [0, 5],
        "columns": 17,
        "minRows": 17,
        "maxRows": 17,
        "colWidth": 95.06,
        "rowHeight": 57.88,
        "floating": false,
        "swapping": false,
        "pushing": false,
        margin: 0,
        outerMarginTop: 0,
        outerMarginRight: 0,
        outerMarginBottom: 0,
        outerMarginLeft: 5,
        outerMargin: true,
        "draggable": {
          "enabled": false
        },
        "resizable": {
          "enabled": false
        }
      },
      "widgets": []
    };
  }

  /**
   * Loads mockup data with report ID
   * @param action Action (controller) name
   * @param reportid The report OID
   */
  loadTemplateData(action: string, reportid: number) {
    return this.httpClient.get(`${this._widgetResource}/${action}/template?oid=${reportid}`);
  }

  /**
   * Loads mockup data with report ID
   * @param action Action (controller) name
   * @param reportid The report OID
   */
  loadMockData(action: string, reportid: number) {
    return this.httpClient.get(`${this._widgetResource}/${action}/mockup?oid=${reportid}`);
  }

  /**
   * Loads a reflist
   * @param name The reflist name (ref_xyz)
   * @param locale The locale in en-US format
   */
  loadReflist(name: string, locale: string = null) {
    let url = `${this._reflistResource}?reflistName=${name}&withParentNo=false`;
    if (locale) {
      const localeOverride = locale.replace('-', '_');
      url += `&localeOverride=${localeOverride}`;
    }

    return this.httpClient.get(url);
  }

  /**
   * Loads multiple reflists as a dictionary
   * @param name The reflist name (ref_xyz)
   * @param locale The locale in en-US format
   */
  loadReflists(names: string[], locale: string = null): Observable<any> {
    let url = `${this._reflistsResource}?withParentNo=false`;
    if (locale) {
      const localeOverride = locale.replace('-', '_');
      url += `&localeOverride=${localeOverride}`;
    }

    return this.httpClient.post(url, names);
  }

  /**
   * Sends the storeData in a POST request
   * @param _url widget url
   * @param storeData Data to be saved
   */
  save(_url: string, storeData: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.httpClient.post(this._widgetResource, storeData).pipe(take(1)).subscribe({
        next: (data: any) => { resolve(data); },
        error: (error: any) => { reject(error); }
      });
    });
  }

  /**
   * Saves the widget
   * @param widgetUrl Widget URL
   * @param storeData Data to be saved
   */
  saveWidget(widgetUrl: string, storeData: any): Promise<any> {
    const url = widgetUrl ?
      this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', `${this._widgetApi}/${widgetUrl}`)
      : this._widgetResource;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.post(url, storeData).pipe(take(1)).subscribe({
        next: (data: any) => { resolve(data); },
        error: (error: any) => { reject(error); }
      });
    });
  }

  /**
   * Saves the widget
   * @param widgetUrl Widget URL
   * @param storeData Data to be saved
   */
  saveWidgetTemplate(widgetUrl: string, storeData: any): Promise<any> {
    const url = widgetUrl ?
      this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', `${this._widgetApi}/${widgetUrl}/template`)
      : this._widgetResource;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.post(url, storeData).pipe(take(1)).subscribe({
        next: (data: any) => { resolve(data); },
        error: (error: any) => { reject(error); }
      });
    });
  }

  /**
   * Gets the widget info for the widget that has the specified widget Guid
   * @param widgetGuid The widget's guid
   */
  public getWidgetInfo(widgetGuid: string): Promise<WidgetInfo> {
    return new Promise<WidgetInfo>((resolve) => {

      const unregister: Subscription = this.eventBus.subscribeEvent("AngWidgets.getWidgetInfo.answer", (response) => {
        const widgetInfo: WidgetInfo = {
          widgetConfig: response.widgetConfig,
          page: response.page
        };
        resolve(widgetInfo);
        unregister.unsubscribe();
      });

      this.eventBus.broadcastEvent("AngWidgets.getWidgetInfo.question", widgetGuid);
    });
  }
}
